import React, { useState, useEffect, useRef } from 'react';

import {
    Box, Grid, TextField, Button, Typography, Alert, Snackbar, CircularProgress, Backdrop, Dialog, DialogTitle, DialogContent,
    DialogActions, Skeleton, Chip, Divider, Autocomplete, Paper, Stack, IconButton
} from '@mui/material';
import { Save, Clear, PictureAsPdf, Email, SaveAlt, Edit as EditIcon, ArrowBack, Person, Map, ListAlt, Hotel, DirectionsCar, CheckCircle, Add, StickyNote2, AttachMoney, SystemUpdateAlt, AddBox, Shield } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllPackages, createPackage, updatePackage } from '../../api/packageAPI';
import { getAllHotels } from '../../api/hotelAPI';
import { getAllLocations } from '../../api/locationAPI';
import { setAllHotels } from '../../reduxcomponents/slices/hotelsSlice';
import { setAllPackages } from '../../reduxcomponents/slices/packagesSlice';
import { setSelectedInquiry } from '../../reduxcomponents/slices/inquirySlice';
import axios from '../../api/interceptor';
// import PdfPreview from './PdfPreview';
import GuestInfoCard from './cards/GuestInfoCard';
import TripDetailsCard from './cards/TripDetailsCard';
// import SelectedPackageCard from './cards/SelectedPackageCard';
import ItineraryCard from './cards/ItineraryCard';
import HotelSelectionCard from './cards/HotelSelectionCard';
import HotelDrawer from '../../components/hotels/HotelDrawer';
import CarSelectionCard from './cards/CarSelectionCard';
import CostEstimateCard from './cards/CostEstimateCard';
import AdditionalInclusionsCard from './cards/AdditionalInclusionsCard';
import InquiryPreview from './InquiryPreview';
import InclusionsExclusionsEditor from './InclusionsExclusionsEditor';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getAllItineraryCards } from '../../api/itineraryCardsAPI';
import {
    filterPackages,
    calculatePackageCost,
    updateItineraryByDuration,
    prePopulateHotelSelections,
    mapFollowupToHotelSelections,
    extractItineraryFromPackage,
    findMatchingItineraryCard,
    validateForm as validateFormData,
    buildPayload as buildPayloadData,
    generatePdfFilename,
    validateDraftRequirements,
    resetHotelSelections,
    updateItineraryItem,
    updateHotelSelection,
    addHotelExtraSelection,
    batchUpdateHotelSelection,
    totalCost,
    totalCostSandakphu,
    hotelCostCalculation,
    calculateCarCost,
    deriveCarDetailsFromSelections,
    sendEmailQuotation,
    useSnackbar,
    resolveApprovedLocation,
} from './createInquiryCalculation';
import { exportQuotationPDF } from './quotationPdf';

export const isPackageVerified = (pkg) => {
    if (!pkg || pkg._id === 'custom') return false;
    return Boolean(pkg.verified || pkg.verifyed || pkg.isVerified);
};

const SECTIONS = [
    { key: 'guest', label: 'Guest Info', icon: <Person sx={{ fontSize: 15 }} />, color: '#667eea' },
    { key: 'trip', label: 'Trip Details', icon: <Map sx={{ fontSize: 15 }} />, color: '#f59e0b' },
    { key: 'itinerary', label: 'Short Itinerary', icon: <ListAlt sx={{ fontSize: 15 }} />, color: '#10b981' },
    { key: 'hotel', label: 'Hotel Selection', icon: <Hotel sx={{ fontSize: 15 }} />, color: '#3b82f6' },
    { key: 'car', label: 'Car Selection', icon: <DirectionsCar sx={{ fontSize: 15 }} />, color: '#8b5cf6' },
    { key: 'inclusions', label: 'Inclusions / Excl.', icon: <CheckCircle sx={{ fontSize: 15 }} />, color: '#06b6d4' },
    { key: 'extras', label: 'Additional Services', icon: <Add sx={{ fontSize: 15 }} />, color: '#f43f5e' },
    { key: 'notes', label: 'Important Notes', icon: <StickyNote2 sx={{ fontSize: 15 }} />, color: '#6366f1' },
    { key: 'cost', label: 'Cost Estimate', icon: <AttachMoney sx={{ fontSize: 15 }} />, color: '#22c55e' },
];

const CreateInquiry = ({ existingInquiry = null, onClose = null }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const reduxSelectedInquiry = useSelector((state) => state.inquiries.fetchSelectedInquiry);
    const allPackages = useSelector((state) => state.package.allPackages || []);
    const allHotels = useSelector((state) => state.hotels.allHotels || []);
    const configData = useSelector((state) => state.config.configData || {});
    const currentUser = useSelector((state) => state.tokens?.user);
    const [savedQuotationNo, setSavedQuotationNo] = useState(
        existingInquiry?.quotation_no || existingInquiry?.quotationNo ||
        reduxSelectedInquiry?.quotation_no || reduxSelectedInquiry?.quotationNo || null
    );
    const [savedInquiryId, setSavedInquiryId] = useState(
        existingInquiry?._id || reduxSelectedInquiry?._id || null
    );
    const isEditMode = Boolean((existingInquiry && (existingInquiry.guest_id || existingInquiry._id)) || (reduxSelectedInquiry && (reduxSelectedInquiry.guest_id || reduxSelectedInquiry._id)) || savedInquiryId);
    const isInitialLoad = useRef(true);
    const loadedInquiryRef = useRef(null); // tracks which inquiry id was last loaded so we never re-load on the same inquiry
    const originalPackageDataRef = useRef(null); // snapshot of package-related fields at load time for change detection
    const [currentMargin, setCurrentMargin] = useState(configData?.additionalCosts?.margin_cost_percent || 20);
    const [isB2B, setIsB2B] = useState(false);
    const pdfRef = useRef(null);
    const guestNameRef = useRef(null);
    const ignoreSuggestionsRef = useRef(false);

    // Sync margin when config loads
    useEffect(() => {
        if (configData?.additionalCosts?.margin_cost_percent && !isEditMode) {
            setCurrentMargin(configData.additionalCosts.margin_cost_percent);
        }
    }, [configData, isEditMode]);

    // Focus guest name on open
    useEffect(() => {
        if (!existingInquiry) {
            setTimeout(() => {
                guestNameRef.current?.focus();
            }, 500);
        }
        return () => {
            dispatch(setSelectedInquiry({}));
        };
    }, [existingInquiry, dispatch]);

    // UI States
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [itineraryDialogOpen, setItineraryDialogOpen] = useState(false);
    const [detailedItinerary, setDetailedItinerary] = useState('');
    const [dayWiseDetailedItinerary, setDayWiseDetailedItinerary] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [storedItineraries, setStoredItineraries] = useState([]);
    const [pkgChangeDialogOpen, setPkgChangeDialogOpen] = useState(false);
    const [hotelDrawerOpen, setHotelDrawerOpen] = useState(false);
    const [selectedHotelToView, setSelectedHotelToView] = useState(null);

    const [inclusions, setInclusions] = useState([
        'Non A/C CAB',
        'All accommodation as per itinerary',
        'All transfer from Bagdogra to Bagdogra',
        'All sightseeing as per itinerary in reserved cab',
        'All permits, tolls, taxes, driver allowance',
        'Breakfast at whole trip',
        '24 X 7 Coordination support'
    ]);
    const [exclusions, setExclusions] = useState([
        'Any air/train fare',
        'Any meals other than mentioned',
        'Any extra services availed at the hotels(Heater, Laundry etc)',
        'Any monumental charges or entry fees',
        'Car at disposal',
        'Anything not mentioned in the inclusion'
    ]);

    // Form States
    const [guestInfo, setGuestInfo] = useState({
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        guest_address: '',
        country_code: '+91',
        lead_source: 'offline'
    });

    const [tripDetails, setTripDetails] = useState({
        pax: '',
        kids_above_5: 0,
        car_details: [], // Array of {car_name, car_count}
        location: '',
        keywords: '',
        travel_date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
        duration: '',
        pickup_location: 'NJP / IXB',
        dropoff_location: 'NJP / IXB',
        optional_extras: [], // Array of {name, price}
        include_extras_cost: true,
        important_notes: '',
    });

    const [selectedPackage, setSelectedPackage] = useState(null);
    const [hotelSelections, setHotelSelections] = useState({});
    const [carSelections, setCarSelections] = useState({});
    const [cost, setCost] = useState(0);
    const [stayInfo, setStayInfo] = useState({
        rooms: '',
        extra_mattress: '',
        hotel: '',
        room_type: '',
    });

    // Package Suggestions
    const [packageSuggestions, setPackageSuggestions] = useState([]);
    const [uniqueLocations, setUniqueLocations] = useState([]);
    const isLocationValid = Boolean(
        tripDetails.location && (
            uniqueLocations.length === 0 ||
            uniqueLocations.some(
                l => (typeof l === 'string' ? l : l.name)?.trim().toLowerCase() === tripDetails.location.trim().toLowerCase()
            ) ||
            Boolean(resolveApprovedLocation(tripDetails.location, uniqueLocations))
        )
    );
    const [itinerary, setItinerary] = useState([]);

    // Tab state for hotel selection
    const [selectedDay, setSelectedDay] = useState(0);
    const [carSeason, setCarSeason] = useState('');
    const [hotelSeason, setHotelSeason] = useState('');

    // Fetch packages and hotels on mount
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchStore = async () => {
            try {
                const res = await getAllItineraryCards();
                if (res.success) setStoredItineraries(res.data);
            } catch (err) {
                console.error("Failed to load itinerary cards", err);
            }
        };
        fetchStore();
    }, []);

    // Load existing inquiry data if in edit mode.
    // Wait until allPackages is populated so package lookup works on first run.
    // Use loadedInquiryRef to ensure the same inquiry is never re-loaded once loaded
    // (avoids wiping user edits when allPackages dispatch changes the dependency).
    useEffect(() => {
        if (!allPackages.length) return; // wait for packages before loading

        const inquiryToLoad = existingInquiry || (Object.keys(reduxSelectedInquiry || {}).length > 0 ? reduxSelectedInquiry : null);
        if (!inquiryToLoad) return;

        const inquiryId = inquiryToLoad._id || JSON.stringify(inquiryToLoad).slice(0, 80);
        if (loadedInquiryRef.current === inquiryId) return; // already loaded this inquiry — don't overwrite edits

        loadedInquiryRef.current = inquiryId;
        loadExistingInquiry(inquiryToLoad);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingInquiry, reduxSelectedInquiry, allPackages.length]);

    // Keep tripDetails.location synchronized with approved master locations once uniqueLocations loads
    useEffect(() => {
        if (!uniqueLocations || uniqueLocations.length === 0) return;

        const inquiryToLoad = existingInquiry || (Object.keys(reduxSelectedInquiry || {}).length > 0 ? reduxSelectedInquiry : null);
        const candidateLoc = (
            tripDetails.location ||
            inquiryToLoad?.destination ||
            inquiryToLoad?.location ||
            inquiryToLoad?.trip_details?.location ||
            (typeof inquiryToLoad?.package_id === 'object' ? inquiryToLoad?.package_id?.location : '') ||
            selectedPackage?.location ||
            ''
        ).toString().trim();

        if (!candidateLoc) return;

        const approvedMatch = resolveApprovedLocation(candidateLoc, uniqueLocations);
        if (approvedMatch && approvedMatch !== tripDetails.location) {
            console.log('[CreateInquiry] Synchronized location to approved master:', approvedMatch);
            setTripDetails(prev => ({ ...prev, location: approvedMatch }));
        }
    }, [uniqueLocations, existingInquiry, reduxSelectedInquiry, selectedPackage?.location]);

    const fetchData = async () => {
        setDataLoading(true);
        try {
            const [packagesRes, hotelsRes, locationsRes] = await Promise.all([
                getAllPackages(),
                getAllHotels({ isActive: true }),
                getAllLocations({ isActive: true }).catch(err => {
                    console.warn('Failed to fetch active locations from master:', err);
                    return { data: [] };
                })
            ]);

            const rawHotels = Array.isArray(hotelsRes.data)
                ? hotelsRes.data
                : (hotelsRes.data?.data || []);
            const activeHotels = rawHotels.filter(h => h.isActive !== false);

            // Save to Redux
            dispatch(setAllPackages(packagesRes.data));
            dispatch(setAllHotels(activeHotels));

            // Extract unique locations ONLY from approved Location Master
            const masterLocations = Array.isArray(locationsRes?.data)
                ? locationsRes.data.map(l => (typeof l === 'string' ? l : l.name)).filter(Boolean)
                : [];

            setUniqueLocations(masterLocations);

            showSnackbar('Data loaded successfully', 'success');
        } catch (error) {
            console.error('Error fetching data:', error);
            showSnackbar('Failed to load data. Please refresh.', 'error');
        } finally {
            setDataLoading(false);
        }
    };

    const loadExistingInquiry = (inquiry) => {
        // Load quotation ID & query ID if present
        const qNo = inquiry.quotation_no || inquiry.quotationNo || inquiry.quote_id || inquiry.quoteNo || null;
        if (qNo) setSavedQuotationNo(qNo);
        if (inquiry._id && (inquiry.guest_id || qNo)) setSavedInquiryId(inquiry._id);

        // Load guest info
        const gName = inquiry.guest_info?.guest_name || inquiry.guest_name || '';
        const gEmail = inquiry.guest_info?.guest_email || inquiry.guest_email || '';
        let gPhone = inquiry.guest_info?.guest_phone || inquiry.guest_phone || '';
        const gAddress = inquiry.guest_info?.guest_address || inquiry.guest_address || '';
        const gCountryCode = inquiry.guest_info?.guest_country_code || inquiry.guest_country_code || inquiry.country_code || '+91';
        const gLeadSource = inquiry.lead_source || 'website';

        // Separate country code from phone number if it's "punched"
        if (gPhone.startsWith(gCountryCode)) {
            gPhone = gPhone.replace(gCountryCode, '').trim();
        } else if (gPhone.startsWith('+') && !inquiry.guest_info?.guest_country_code && !inquiry.guest_country_code) {
            // If it starts with + but we didn't have a separate country code, try to guess it or just leave as if we can't be sure
            // For now, if we have gCountryCode (default or from data), use it to strip.
        }

        setGuestInfo({
            guest_name: gName,
            guest_email: gEmail,
            guest_phone: gPhone,
            guest_address: gAddress,
            country_code: gCountryCode,
            lead_source: gLeadSource,
        });
        setEmailAddress(gEmail);

        const duration = parseInt(inquiry.duration) || 0;
        const totalDays = duration + 1;
        const prunedCarSelections = {};
        if (inquiry.car_selections && typeof inquiry.car_selections === 'object') {
            for (let d = 0; d < totalDays; d++) {
                if (inquiry.car_selections[d] || inquiry.car_selections[String(d)]) {
                    prunedCarSelections[d] = inquiry.car_selections[d] || inquiry.car_selections[String(d)];
                }
            }
        }
        const derivedFromSelections = deriveCarDetailsFromSelections(prunedCarSelections, totalDays);
        const loadedCarDetails = derivedFromSelections.length > 0
            ? derivedFromSelections
            : (inquiry.car_details || []);

        // Resolve package first if available to help identify destination
        const pkgId = inquiry.package_id?._id || inquiry.package_id;
        let loadedPkg = null;
        if (pkgId && allPackages.length > 0) {
            loadedPkg = allPackages.find(p => p._id.toString() === pkgId.toString());
        }
        if (!loadedPkg && inquiry.package_id && typeof inquiry.package_id === 'object' && inquiry.package_id._id) {
            loadedPkg = inquiry.package_id;
        }

        const rawCandidateLoc = (
            inquiry.destination ||
            inquiry.location ||
            inquiry.trip_details?.location ||
            (typeof inquiry.package_id === 'object' ? inquiry.package_id?.location : '') ||
            loadedPkg?.location ||
            ''
        ).toString().trim();

        const finalLoadedLocation = resolveApprovedLocation(rawCandidateLoc, uniqueLocations) || rawCandidateLoc;

        const tripDetailsObj = {
            pax: (inquiry.pax || inquiry.guest_count || '')?.toString() || '',
            kids_above_5: inquiry.kids_above_5 || 0,
            car_details: loadedCarDetails,
            location: finalLoadedLocation,
            keywords: '',
            travel_date: (inquiry.travel_date || inquiry.arrival_date) ? new Date(inquiry.travel_date || inquiry.arrival_date).toISOString().split('T')[0] : '',
            duration: inquiry.duration?.toString() || '',
            pickup_location: inquiry.pickup_location || 'NJP / IXB',
            dropoff_location: inquiry.dropoff_location || 'NJP / IXB',
            optional_extras: [
                ...(Array.isArray(inquiry.optional_extras) ? inquiry.optional_extras : []),
                ...(Array.isArray(inquiry.details?.cost?.optionalExtras) ? inquiry.details.cost.optionalExtras : []),
                ...(Array.isArray(inquiry.trip_details?.optional_extras) ? inquiry.trip_details.optional_extras : [])
            ].map(e => {
                const name = typeof e === 'object' ? (e.name || e.tagValue || e.tagName || e.label || '') : e;
                const price = typeof e === 'object' ? (e.price || 0) : 0;
                const count = typeof e === 'object' ? (e.count || 1) : 1;
                return { name: (name || '').trim(), price: Number(price || 0), count: Number(count || 1) };
            }).filter((item, index, self) =>
                item.name && self.findIndex(t => t.name.toLowerCase() === item.name.toLowerCase()) === index
            ),
            include_extras_cost: inquiry.trip_details?.include_extras_cost !== false,
            important_notes: inquiry.important_notes || '',
        };

        console.log("Final Trip Details loaded:", tripDetailsObj);
        setTripDetails(tripDetailsObj);

        // Load stay info
        if (inquiry.stay_info) {
            setStayInfo({
                rooms: (parseInt(inquiry.stay_info.rooms) > 0) ? inquiry.stay_info.rooms.toString() : '',
                extra_mattress: inquiry.stay_info.extra_mattress !== undefined ? inquiry.stay_info.extra_mattress.toString() : '',
                hotel: inquiry.stay_info.hotel || '',
                room_type: inquiry.stay_info.room_type || '',
            });
        }

        // Load cost
        setCost(inquiry.cost || 0);
        setCarSeason(inquiry.car_season || 'off_season_price');
        setHotelSeason(inquiry.hotel_season || 'off_season_price');
        if (inquiry.margin !== undefined) {
            setCurrentMargin(inquiry.margin);
        }
        setIsB2B(Boolean(inquiry.is_b2b));

        // Load itinerary - Priority: 1. inquiry.itinerary (if complete), 2. followup_details, 3. empty slots
        if (inquiry.itinerary && Array.isArray(inquiry.itinerary) && inquiry.itinerary.length > 1) {
            setItinerary(inquiry.itinerary);
        } else if (inquiry.followup_details && inquiry.followup_details.length > 0) {
            const manualItinerary = inquiry.followup_details.map(item => item.destination || '');
            const duration = parseInt(inquiry.duration) || 0;
            setItinerary(updateItineraryByDuration(manualItinerary, duration));
        } else if (inquiry.itinerary && Array.isArray(inquiry.itinerary) && inquiry.itinerary.length > 0) {
            setItinerary(inquiry.itinerary);
        } else if (inquiry.duration) {
            // If no itinerary but duration exists, create empty slots
            setItinerary(new Array(parseInt(inquiry.duration) + 1).fill(''));
        }

        // Load package - If package exists, load its full descriptive itinerary
        let extractedItinerary = [];
        if (loadedPkg) {
            // Use handlePackageSelect logic to populate itinerary and hotels
            setSelectedPackage(loadedPkg);

            if (!tripDetailsObj.location && loadedPkg.location) {
                const pkgLoc = resolveApprovedLocation(loadedPkg.location, uniqueLocations) || loadedPkg.location;
                tripDetailsObj.location = pkgLoc;
                setTripDetails(prev => ({ ...prev, location: pkgLoc }));
            }

            // Set editable short itinerary from package (if not already set or partial)
            extractedItinerary = extractItineraryFromPackage(loadedPkg.details);
            const hasExistingItinerary = inquiry.itinerary?.length > 1 || (inquiry.followup_details?.length > 1);

            if (extractedItinerary.length > 0 && !hasExistingItinerary) {
                setItinerary(extractedItinerary);
            }

            // If hotel selections are missing in the inquiry, pre-populate from package
            if (!inquiry.hotel_selections || Object.keys(inquiry.hotel_selections).length === 0) {
                if (loadedPkg.details?.shortItinerary && Array.isArray(loadedPkg.details.shortItinerary)) {
                    const newHotelSelections = prePopulateHotelSelections(loadedPkg.details.shortItinerary, allHotels);
                    setHotelSelections(newHotelSelections);
                }
            }
        }

        // Helper to normalize a hotel selection row
        const normalizeRow = (s) => ({
            ...s,
            hotelId: (typeof s?.hotelId === 'object' ? s?.hotelId?._id : s?.hotelId)?.toString() || '',
            roomType: s?.roomType || s?.room_type || '',
            mealPlan: s?.mealPlan || s?.meal_plan || '',
            roomsCount: parseInt(s?.roomsCount) || (parseInt(inquiry.stay_info?.rooms) > 0 ? parseInt(inquiry.stay_info.rooms) : 1),
            location: s?.location || s?.destination || '',
        });

        // Load hotel selections if they exist (overrides package defaults)
        if (inquiry.hotel_selections && Object.keys(inquiry.hotel_selections).length > 0) {
            const normalizedSelections = {};
            Object.keys(inquiry.hotel_selections).forEach(day => {
                const dayData = inquiry.hotel_selections[day];
                if (dayData && !Array.isArray(dayData) && Object.keys(dayData).some(k => !isNaN(k))) {
                    normalizedSelections[day] = Object.keys(dayData)
                        .filter(k => !isNaN(k))
                        .sort((a, b) => Number(a) - Number(b))
                        .map(k => normalizeRow(dayData[k]));
                } else if (dayData && !Array.isArray(dayData)) {
                    normalizedSelections[day] = [normalizeRow(dayData)];
                } else if (Array.isArray(dayData)) {
                    normalizedSelections[day] = dayData.map(normalizeRow);
                } else {
                    normalizedSelections[day] = dayData;
                }
            });
            setHotelSelections(normalizedSelections);
        } else if (inquiry.followup_details && inquiry.followup_details.length > 0) {
            const reconstructedHotels = mapFollowupToHotelSelections(inquiry.followup_details, allHotels);
            setHotelSelections(reconstructedHotels);
        }

        // Load car selections
        if (inquiry.car_selections && Object.keys(inquiry.car_selections).length > 0) {
            setCarSelections(prunedCarSelections);
        }

        // Restore inclusions / exclusions if saved
        if (inquiry.inclusions && Array.isArray(inquiry.inclusions) && inquiry.inclusions.length > 0) {
            setInclusions(inquiry.inclusions);
        }
        if (inquiry.exclusions && Array.isArray(inquiry.exclusions) && inquiry.exclusions.length > 0) {
            setExclusions(inquiry.exclusions);
        }

        // Restore day-wise detailed itinerary if saved (support both snake_case and camelCase)
        const savedDayWise = inquiry.day_wise_detailed_itinerary || inquiry.dayWiseDetailedItinerary;
        if (savedDayWise && Array.isArray(savedDayWise) && savedDayWise.length > 0) {
            setDayWiseDetailedItinerary(savedDayWise);

            // Convert day-wise itinerary to HTML for the detailed_itinerary editor
            let html = '';
            savedDayWise.forEach((dayDetail, idx) => {
                if (dayDetail) {
                    const dayTitle = dayDetail.dayTitle || `Day ${idx + 1}`;
                    html += `<h3>${dayTitle}</h3>`;
                    if (dayDetail.para && Array.isArray(dayDetail.para)) {
                        dayDetail.para.forEach(p => {
                            html += p;
                        });
                    }
                }
            });
            if (html) {
                setDetailedItinerary(html);
            }
        } else if (inquiry.detailed_itinerary || inquiry.detailedItinerary) {
            // Fallback to string-based detailed itinerary if day-wise doesn't exist
            setDetailedItinerary(inquiry.detailed_itinerary || inquiry.detailedItinerary);
        }

        // Snapshot package-related fields so we can detect changes later
        const initItinerary = (inquiry.itinerary && inquiry.itinerary.length > 0)
            ? inquiry.itinerary
            : (extractedItinerary || []);
        const initInclusions = (inquiry.inclusions && inquiry.inclusions.length > 0)
            ? inquiry.inclusions
            : (loadedPkg?.details?.cost?.inclusions || []);
        const initExclusions = (inquiry.exclusions && inquiry.exclusions.length > 0)
            ? inquiry.exclusions
            : (loadedPkg?.details?.cost?.exclusions || []);

        originalPackageDataRef.current = {
            itinerary: JSON.stringify(initItinerary || []),
            inclusions: JSON.stringify(initInclusions || []),
            exclusions: JSON.stringify(initExclusions || []),
            duration: inquiry.duration ? String(inquiry.duration) : (loadedPkg?.duration ? String(loadedPkg.duration) : '0'),
            location: finalLoadedLocation || inquiry.destination || inquiry.location || loadedPkg?.location || '',
        };
    };

    // Advanced package filtering based on duration, location, and keyword-in-tags match
    useEffect(() => {
        if (!allPackages.length) {
            setPackageSuggestions([]);
            return;
        }

        const filtered = filterPackages(allPackages, tripDetails);
        setPackageSuggestions(filtered);

        if (ignoreSuggestionsRef.current) {
            ignoreSuggestionsRef.current = false;
            setShowSuggestions(false);
        } else if (filtered.length > 0 && (tripDetails.duration || tripDetails.location || tripDetails.keywords)) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    }, [tripDetails.duration, tripDetails.location, tripDetails.keywords, allPackages]);

    // Update itinerary based on duration
    useEffect(() => {
        if (isInitialLoad.current && isEditMode) {
            isInitialLoad.current = false;
            return;
        }
        setItinerary(prev => updateItineraryByDuration(prev, tripDetails.duration));

        const duration = parseInt(tripDetails.duration) || 0;
        const totalDays = duration + 1;
        setCarSelections(prev => {
            if (!prev || Object.keys(prev).length === 0) return prev;
            const pruned = {};
            for (let d = 0; d < totalDays; d++) {
                if (prev[d] || prev[String(d)]) {
                    pruned[d] = prev[d] || prev[String(d)];
                }
            }
            const derived = deriveCarDetailsFromSelections(pruned, totalDays);
            setTripDetails(tPrev => ({
                ...tPrev,
                car_details: derived
            }));
            return pruned;
        });
    }, [tripDetails.duration, isEditMode]);

    useEffect(() => {
        const extrasCost = (tripDetails.optional_extras || []).reduce((acc, extra) => acc + ((extra.price || 0) * (extra.count || 1)), 0);
        const extrasCostForCalc = (tripDetails.optional_extras || []).reduce((acc, extra) => {
            return acc + (extra.include_in_cost !== false ? ((extra.price || 0) * (extra.count || 1)) : 0);
        }, 0);
        let tCost = 0;
        if (tripDetails.location && tripDetails.location !== 'Sandakphu') {
            const hCost = hotelCostCalculation(hotelSelections, allHotels, hotelSeason, tripDetails, stayInfo);
            const cCost = calculateCarCost(configData, carSeason, tripDetails, carSelections);
            tCost = totalCost(hCost, cCost, currentMargin, extrasCostForCalc);
            setCost(tCost);
        } else if (tripDetails.location === 'Sandakphu') {
            const hCostSandakphu = hotelCostCalculation(hotelSelections, allHotels, hotelSeason, tripDetails, stayInfo);
            const cCostSandakphu = calculateCarCost(configData, carSeason, tripDetails, carSelections);
            tCost = totalCostSandakphu(hCostSandakphu, cCostSandakphu, currentMargin, tripDetails, selectedPackage);
            setCost(tCost + extrasCostForCalc);
        }
    }, [hotelSelections, carSelections, tripDetails.car_details, tripDetails.duration, tripDetails.pax, tripDetails.kids_above_5, carSeason, hotelSeason, currentMargin, allHotels, configData, stayInfo, stayInfo.rooms, stayInfo.extra_mattress, tripDetails.location, itinerary, selectedPackage, tripDetails.optional_extras, tripDetails.include_extras_cost]);

    const handleGuestInfoChange = (e) => {
        const { name, value } = e.target;
        setGuestInfo({
            ...guestInfo,
            [name]: value,
        });
        // Auto-fill email address for email dialog
        if (name === 'guest_email') {
            setEmailAddress(value);
        }
    };

    const handleTripDetailsChange = (e) => {
        if (e.target.name === 'location') {
            handleLocationChange(e.target.value);
            return;
        }
        setTripDetails({
            ...tripDetails,
            [e.target.name]: e.target.value,
        });
    };

    const handleStayInfoChange = (e) => {
        setStayInfo({
            ...stayInfo,
            [e.target.name]: e.target.value,
        });
    };

    const handlePackageSelect = (pkg) => {
        ignoreSuggestionsRef.current = true;
        setShowSuggestions(false);

        if (!pkg || pkg._id === 'custom') {
            setSelectedPackage({ _id: 'custom', label: 'Custom Package', location: tripDetails.location });
            originalPackageDataRef.current = null;
            // Don't clear itinerary here, let the useEffect handle it based on duration
            // or keep existing if switching back
            setHotelSelections({});
            setCost(0);
            return;
        }

        setSelectedPackage(pkg);
        if (pkg.location) {
            const approvedMatch = resolveApprovedLocation(pkg.location, uniqueLocations) || pkg.location;
            if (approvedMatch) {
                setTripDetails(prev => ({ ...prev, location: approvedMatch }));
            }
        }
        if (pkg.duration) {
            setTripDetails(prev => ({ ...prev, duration: pkg.duration.toString() }));
        }

        // Set editable short itinerary
        const extractedItinerary = extractItineraryFromPackage(pkg.details);
        setItinerary(extractedItinerary);

        // Auto-populate full day-wise detailed itinerary from package details or storedItineraries
        const newDetailed = (extractedItinerary || []).map((dayTitle, idx) => {
            const pkgDay = pkg.details?.itinerary?.[idx];
            const pkgPara = Array.isArray(pkgDay?.para) ? pkgDay.para : (pkgDay?.para ? [pkgDay.para] : []);
            if (pkgPara.length > 0 && pkgPara.some(p => p && p.trim())) {
                return {
                    dayTitle,
                    para: pkgPara,
                    isAutoPopulated: true
                };
            }
            const matched = findMatchingItineraryCard(dayTitle, storedItineraries, allPackages);
            if (matched && matched.description) {
                return {
                    dayTitle,
                    para: [matched.description],
                    isAutoPopulated: true,
                    matchedCardTitle: matched.title
                };
            }
            return { dayTitle, para: [] };
        });
        setDayWiseDetailedItinerary(newDetailed);

        // Load inclusions / exclusions from package if available
        let pkgInclusions = inclusions;
        let pkgExclusions = exclusions;
        if (pkg.details?.cost?.inclusions && Array.isArray(pkg.details.cost.inclusions) && pkg.details.cost.inclusions.length > 0) {
            pkgInclusions = pkg.details.cost.inclusions;
            setInclusions(pkg.details.cost.inclusions);
        }
        if (pkg.details?.cost?.exclusions && Array.isArray(pkg.details.cost.exclusions) && pkg.details.cost.exclusions.length > 0) {
            pkgExclusions = pkg.details.cost.exclusions;
            setExclusions(pkg.details.cost.exclusions);
        }

        // Extract locations from shortItinerary and pre-populate hotel selections
        if (pkg.details?.shortItinerary && Array.isArray(pkg.details.shortItinerary)) {
            const newHotelSelections = prePopulateHotelSelections(pkg.details.shortItinerary, allHotels);
            setHotelSelections(newHotelSelections);
        }

        // Set baseline snapshot of selected package for accurate change detection
        originalPackageDataRef.current = {
            itinerary: JSON.stringify(extractedItinerary || []),
            inclusions: JSON.stringify(pkgInclusions || []),
            exclusions: JSON.stringify(pkgExclusions || []),
            duration: pkg.duration ? String(pkg.duration) : String(tripDetails.duration || '0'),
            location: pkg.location || tripDetails.location || '',
        };

        setPackageSuggestions([]);
    };

    const handleGuestReset = () => {
        setGuestInfo({
            guest_name: '',
            guest_email: '',
            guest_phone: '',
            guest_address: '',
            country_code: '+91'
        });
        setTripDetails(prev => ({
            ...prev,
            pax: '',
            kids_above_5: 0,
        }));
    };

    const handleTripDetailsReset = () => {
        setTripDetails(prev => ({
            ...prev,
            location: '',
            keywords: '',
            car_details: [],
            travel_date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
            duration: '0',
            include_extras_cost: true,
            important_notes: ''
        }));
        setStayInfo({ rooms: '', hotel: '' });
        setSelectedPackage(null);
        setCost(0);
        setItinerary([]);
        setHotelSelections({});
        setCarSeason('');
        setHotelSeason('');
    };

    const handleLocationChange = (newValue) => {
        const rawName = newValue ? (typeof newValue === 'string' ? newValue : newValue.name || '') : '';
        const trimmed = rawName.trim();
        const approvedMatch = resolveApprovedLocation(trimmed, uniqueLocations) || trimmed;

        setTripDetails(prev => ({ ...prev, location: approvedMatch, duration: 0 }));
        setHotelSelections({});
        setCarSelections({});
        if (!approvedMatch) {
            setSelectedPackage(null);
            setItinerary([]);
        } else {
            setSelectedPackage({ _id: 'custom', label: 'Custom Package', location: approvedMatch });
        }
    };

    const handleHotelReset = () => {
        setHotelSeason('');
        setHotelSelections(prev => resetHotelSelections(prev));
    };

    const handleCarReset = () => {
        setCarSeason('');
        setCarSelections({});
        setTripDetails(prev => ({ ...prev, car_details: [] }));
    };

    const handleHotelChange = (dayIndex, roomIndex, field, value) => {
        setHotelSelections(prev => updateHotelSelection(prev, dayIndex, roomIndex, field, value));
    };

    const handleHotelBatchChange = (dayIndex, roomIndex, fields) => {
        setHotelSelections(prev => batchUpdateHotelSelection(prev, dayIndex, roomIndex, fields));
    };

    const handleAddHotelRoom = (dayIndex) => {
        setHotelSelections(prev => {
            const { addHotelRoomSelection } = require('./createInquiryCalculation');
            return addHotelRoomSelection(prev, dayIndex);
        });
    };

    const handleAddHotelExtra = (dayIndex) => {
        setHotelSelections(prev => addHotelExtraSelection(prev, dayIndex, tripDetails, stayInfo));
    };

    const handleRemoveHotelRoom = (dayIndex, roomIndex) => {
        setHotelSelections(prev => {
            const { removeHotelRoomSelection } = require('./createInquiryCalculation');
            return removeHotelRoomSelection(prev, dayIndex, roomIndex);
        });
    };

    const handleCostChange = (e) => {
        setCost(parseFloat(e.target.value) || 0);
    };

    const handleCarSeasonChange = (e) => setCarSeason(e.target.value);
    const handleHotelSeasonChange = (e) => setHotelSeason(e.target.value);

    const handleOptionalExtraToggle = (extraName, price, count) => {
        setTripDetails(prev => {
            const currentExtras = prev.optional_extras || [];
            const existingIndex = currentExtras.findIndex(e =>
                (e.name || '').toLowerCase().trim() === extraName.toLowerCase().trim()
            );
            let newExtras = [...currentExtras];

            if (count !== undefined) {
                if (count === 0) {
                    if (existingIndex >= 0) newExtras.splice(existingIndex, 1);
                } else {
                    if (existingIndex >= 0) {
                        newExtras[existingIndex] = { ...newExtras[existingIndex], count };
                    } else {
                        // Default to true for include_in_cost when adding a new extra
                        newExtras.push({ name: extraName.trim(), price, count, include_in_cost: true });
                    }
                }
            } else {
                if (existingIndex >= 0) {
                    newExtras.splice(existingIndex, 1);
                } else {
                    newExtras.push({ name: extraName.trim(), price, count: 1, include_in_cost: true });
                }
            }
            return { ...prev, optional_extras: newExtras };
        });
    };

    const handleOptionalExtraIncludeToggle = (extraName, include_in_cost) => {
        setTripDetails(prev => {
            const currentExtras = prev.optional_extras || [];
            const existingIndex = currentExtras.findIndex(e =>
                (e.name || '').toLowerCase().trim() === extraName.toLowerCase().trim()
            );
            if (existingIndex >= 0) {
                let newExtras = [...currentExtras];
                newExtras[existingIndex] = { ...newExtras[existingIndex], include_in_cost };
                return { ...prev, optional_extras: newExtras };
            }
            return prev;
        });
    };

    const handleItineraryChange = (index, value) => {
        setItinerary(prev => updateItineraryItem(prev, index, value));
    };

    const handleCarDetailsChange = (carName, newCount) => {
        setTripDetails(prev => {
            const existingCarIndex = prev.car_details.findIndex(car => car.car_name === carName);
            let newCarDetails = [...prev.car_details];

            if (newCount <= 0) {
                // Remove car if count is 0 or less
                if (existingCarIndex !== -1) {
                    newCarDetails.splice(existingCarIndex, 1);
                }
            } else {
                // Update existing or add new car
                if (existingCarIndex !== -1) {
                    newCarDetails[existingCarIndex] = { ...newCarDetails[existingCarIndex], car_count: newCount };
                } else {
                    newCarDetails.push({ car_name: carName, car_count: newCount });
                }
            }

            return { ...prev, car_details: newCarDetails };
        });
    };

    const handleCarSelectionChange = (dayIndex, carName, newCount) => {
        setCarSelections(prev => {
            const dayCars = [...(prev[dayIndex] || [])];
            const existingIndex = dayCars.findIndex(c => c.car_name === carName);

            if (newCount <= 0) {
                if (existingIndex !== -1) dayCars.splice(existingIndex, 1);
            } else {
                if (existingIndex !== -1) {
                    dayCars[existingIndex] = { ...dayCars[existingIndex], car_count: newCount };
                } else {
                    dayCars.push({ car_name: carName, car_count: newCount });
                }
            }

            const updated = { ...prev, [dayIndex]: dayCars };
            const totalDays = (parseInt(tripDetails.duration) || 0) + 1;
            const derived = deriveCarDetailsFromSelections(updated, totalDays);
            setTripDetails(tPrev => ({
                ...tPrev,
                car_details: derived
            }));
            return updated;
        });
    };

    const handleViewHotel = (hotelId) => {
        const hotel = allHotels.find(h => h._id === hotelId);
        if (hotel) {
            setSelectedHotelToView(hotel);
            setHotelDrawerOpen(true);
        }
    };

    const validateForm = () => {
        const validation = validateFormData(guestInfo, tripDetails, selectedPackage, isEditMode, uniqueLocations);
        if (!validation.isValid) {
            showSnackbar(validation.message, validation.severity);
            return false;
        }
        return true;
    };

    const buildPayload = (isDraft = false) => {
        return buildPayloadData({
            guestInfo,
            tripDetails,
            selectedPackage,
            cost,
            stayInfo,
            hotelSelections,
            carSelections,
            allHotels,
            hotelSeason,
            carSeason,
            margin: currentMargin,
            itinerary,
            inclusions,
            exclusions,
            detailedItinerary,
            dayWiseDetailedItinerary,
            isDraft,
            isB2B
        });
    };

    const handleSubmit = async (isDraft = false) => {
        if (!isDraft && !validateForm()) {
            return null;
        }

        setLoading(true);

        let payload = buildPayload(isDraft);
        const QRY_URL = `${process.env.REACT_APP_BASE_URL}/queries`;

        //important code
        try {
            const inquiryId = savedInquiryId || existingInquiry?._id || (isEditMode ? reduxSelectedInquiry?._id : null);
            let savedQuery = null;
            if ((isEditMode || savedInquiryId) && inquiryId) {
                const res = await axios.put(`${QRY_URL}/update/${inquiryId}`, payload);
                savedQuery = res.data?.data || res.data;
                showSnackbar('Inquiry updated successfully!', 'success');
            } else {
                const res = await axios.post(`${QRY_URL}/create-queries`, payload);
                savedQuery = res.data?.data || res.data;
                showSnackbar(
                    isDraft ? 'Draft saved successfully!' : 'Inquiry created successfully!',
                    'success'
                );
            }

            if (savedQuery?.quotation_no) {
                setSavedQuotationNo(savedQuery.quotation_no);
            }
            if (savedQuery?._id) {
                setSavedInquiryId(savedQuery._id);
                dispatch(setSelectedInquiry(savedQuery));
            }

            if (!isDraft) {
                setTimeout(() => {
                    if (onClose) {
                        onClose();
                    } else {
                        resetForm();
                    }
                }, 1500);
            }

            return savedQuery;
        } catch (error) {
            console.error('Error saving inquiry:', error);
            showSnackbar(
                error.response?.data?.message || 'Failed to save inquiry',
                'error'
            );
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePackage = async () => {
        if (!guestInfo.guest_name) {
            showSnackbar('Please enter guest name first', 'warning');
            return;
        }
        if (!tripDetails.location) {
            showSnackbar('Please select a location first', 'warning');
            return;
        }
        if (!tripDetails.duration || parseInt(tripDetails.duration) === 0) {
            showSnackbar('Please enter trip duration (nights)', 'warning');
            return;
        }

        setLoading(true);
        console.log('Creating custom package with payload:', {
            guestName: guestInfo.guest_name,
            location: tripDetails.location,
            duration: tripDetails.duration,
            itineraryCount: itinerary.length
        });

        try {
            const customPackagePayload = {
                label: `Custom Package - ${guestInfo.guest_name}`,
                location: tripDetails.location || '',
                duration: tripDetails.duration || '0',
                type: 'Custom',
                url: `custom-package-${Date.now()}`,
                isActive: false,
                image: '',
                tags: [],
                meta: { title: '', description: '', keywords: [] },
                shortInclusions: [],
                details: {
                    header: { h1: '', h2: '', h3: '' },
                    overview: [],
                    covering: [],
                    shortItinerary: itinerary.map(item => ({
                        tagName: '',
                        tagValue: item || ''
                    })),
                    itinerary: [],
                    howToReach: {
                        para: [],
                        itineraryReach: []
                    },
                    cost: {
                        singleCost: '',
                        multipleCost: [],
                        daysCost: [],
                        valueCost: [],
                        inclusions: [],
                        exclusions: []
                    },
                    thingsToCarry: {
                        Basics: [],
                        Documents: [],
                        Clothing: [],
                        Medicine: [],
                        Toiletries: [],
                        Accessories: [],
                    }
                }
            };

            const pkgRes = await createPackage(customPackagePayload);
            const newPkg = pkgRes.data?.data;

            if (newPkg && newPkg._id) {
                // Refresh packages list to include the new one first
                const packagesRes = await getAllPackages();
                dispatch(setAllPackages(packagesRes.data));

                // Then select the new package (find it in the fresh list for consistency)
                const freshPkg = packagesRes.data.find(p => p._id === newPkg._id);
                setSelectedPackage(freshPkg || newPkg);

                showSnackbar('Custom package created and selected!', 'success');
            } else {
                throw new Error('Could not retrieve new package ID');
            }
        } catch (err) {
            console.error('Error creating custom package:', err);
            const errorMsg = err.response?.data?.message || err.message || 'Failed to create custom package';
            showSnackbar(errorMsg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAsDraft = () => {
        const validation = validateDraftRequirements(guestInfo);
        if (!validation.isValid) {
            showSnackbar(validation.message, validation.severity);
            return;
        }
        handleSubmit(true);
    };

    // Returns true if itinerary/inclusions/exclusions changed vs what was loaded
    const hasPackageDataChanged = () => {
        const orig = originalPackageDataRef.current;
        if (!orig) return false;
        return (
            JSON.stringify(itinerary) !== orig.itinerary ||
            JSON.stringify(inclusions) !== orig.inclusions ||
            JSON.stringify(exclusions) !== orig.exclusions ||
            String(tripDetails.duration) !== String(orig.duration) ||
            (tripDetails.location || '') !== (orig.location || '')
        );
    };

    // Full create-package payload (mirrors handleCreatePackage and preserves base package details)
    const buildNewPackagePayload = (label) => {
        const base = (selectedPackage && selectedPackage._id !== 'custom') ? selectedPackage : {};
        return {
            label: label || `Custom Package - ${guestInfo.guest_name || tripDetails.location}`,
            location: tripDetails.location || base.location || '',
            duration: tripDetails.duration || base.duration || '0',
            type: base.type || 'Custom',
            url: `custom-package-${Date.now()}`,
            isActive: false,
            verified: false,
            verifyed: false,
            image: base.image || '',
            tags: base.tags || [],
            meta: base.meta || { title: '', description: '', keywords: [] },
            shortInclusions: base.shortInclusions || [],
            details: {
                header: base.details?.header || { h1: '', h2: '', h3: '' },
                overview: base.details?.overview || [],
                covering: base.details?.covering || [],
                shortItinerary: itinerary.map(item => ({ tagName: '', tagValue: item || '' })),
                itinerary: base.details?.itinerary || [],
                howToReach: base.details?.howToReach || { para: [], itineraryReach: [] },
                cost: {
                    singleCost: base.details?.cost?.singleCost || '',
                    multipleCost: base.details?.cost?.multipleCost || [],
                    daysCost: base.details?.cost?.daysCost || [],
                    valueCost: base.details?.cost?.valueCost || [],
                    inclusions: inclusions || [],
                    exclusions: exclusions || [],
                },
                thingsToCarry: base.details?.thingsToCarry || { Basics: [], Documents: [], Clothing: [], Medicine: [], Toiletries: [], Accessories: [] },
            },
        };
    };

    // Update-existing payload (patches current package with current itinerary/inclusions/exclusions)
    const buildUpdatePackagePayload = () => ({
        ...selectedPackage,
        location: tripDetails.location || selectedPackage?.location || '',
        duration: tripDetails.duration || selectedPackage?.duration || '0',
        details: {
            ...(selectedPackage?.details || {}),
            shortItinerary: itinerary.map(item => ({ tagName: '', tagValue: item || '' })),
            cost: {
                ...(selectedPackage?.details?.cost || {}),
                inclusions: inclusions || [],
                exclusions: exclusions || [],
            },
        },
    });

    // Dialog: "Update existing package" → then open preview
    const handleUpdateExistingPackage = async () => {
        if (isPackageVerified(selectedPackage)) {
            showSnackbar('Verified package cannot be edited. Please create a new package instead.', 'warning');
            return;
        }
        setPkgChangeDialogOpen(false);
        setLoading(true);
        try {
            await updatePackage(buildUpdatePackagePayload(), selectedPackage._id);
            showSnackbar(`Package "${selectedPackage.label}" updated!`, 'success');
            const packagesRes = await getAllPackages();
            dispatch(setAllPackages(packagesRes.data));
            const freshPkg = packagesRes.data?.find(p => p._id === selectedPackage._id);
            if (freshPkg) setSelectedPackage(freshPkg);

            // Update snapshot
            originalPackageDataRef.current = {
                itinerary: JSON.stringify(itinerary || []),
                inclusions: JSON.stringify(inclusions || []),
                exclusions: JSON.stringify(exclusions || []),
                duration: tripDetails.duration ? String(tripDetails.duration) : '0',
                location: tripDetails.location || '',
            };
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to update package', 'error');
        } finally {
            setLoading(false);
        }
        setPreviewOpen(true);
    };

    // Dialog: "Create new package" → then open preview
    const handleCreateNewPackageFromDialog = async () => {
        setPkgChangeDialogOpen(false);
        setLoading(true);
        try {
            const isPkgVerified = isPackageVerified(selectedPackage);
            let label;
            if (selectedPackage && selectedPackage._id !== 'custom') {
                label = isPkgVerified
                    ? `${selectedPackage.label} (Customized)`
                    : `${selectedPackage.label} (Updated)`;
            } else {
                label = `Custom Package - ${guestInfo.guest_name || tripDetails.location}`;
            }

            const pkgRes = await createPackage(buildNewPackagePayload(label));
            const newPkg = pkgRes.data?.data || pkgRes.data;
            if (newPkg?._id) {
                const packagesRes = await getAllPackages();
                dispatch(setAllPackages(packagesRes.data));
                const freshPkg = packagesRes.data?.find(p => p._id === newPkg._id);
                setSelectedPackage(freshPkg || newPkg);

                // Update snapshot to match new package
                originalPackageDataRef.current = {
                    itinerary: JSON.stringify(itinerary || []),
                    inclusions: JSON.stringify(inclusions || []),
                    exclusions: JSON.stringify(exclusions || []),
                    duration: tripDetails.duration ? String(tripDetails.duration) : '0',
                    location: tripDetails.location || '',
                };

                showSnackbar(`Package "${label}" created and linked!`, 'success');
            } else {
                throw new Error('Could not retrieve new package ID');
            }
        } catch (err) {
            showSnackbar(err.response?.data?.message || err.message || 'Failed to create package', 'error');
        } finally {
            setLoading(false);
        }
        setPreviewOpen(true);
    };

    // Dialog: "Save without package changes" → open preview
    const handleSaveWithoutPackageChange = () => {
        setPkgChangeDialogOpen(false);
        setPreviewOpen(true);
    };

    const handleOpenPreview = () => {
        if (!validateForm()) return;
        const hasExistingPkg = selectedPackage && selectedPackage._id !== 'custom';
        // Show package change dialog whenever package-related fields changed for an existing package
        if (hasExistingPkg && hasPackageDataChanged()) {
            setPkgChangeDialogOpen(true);
        } else {
            setPreviewOpen(true);
        }
    };

    const handleExportPDF = () => {
        setItineraryDialogOpen(true);
    };

    const handleConfirmExportPDF = async () => {
        setItineraryDialogOpen(false);
        // DEBUG: log hotel selections snapshot at export time
        console.log('[PDF Export] hotelSelections snapshot:', JSON.parse(JSON.stringify(hotelSelections)));
        console.log('[PDF Export] carSelections snapshot:', JSON.parse(JSON.stringify(carSelections)));

        let activeQuotationNo = savedQuotationNo || existingInquiry?.quotation_no || existingInquiry?.quotationNo || reduxSelectedInquiry?.quotation_no || reduxSelectedInquiry?.quotationNo;
        let activeInquiry = existingInquiry || (savedInquiryId ? { _id: savedInquiryId, quotation_no: activeQuotationNo } : null) || (reduxSelectedInquiry?._id ? reduxSelectedInquiry : null);

        // If creating a new quotation without a quotation number yet, auto-save to backend to generate the official Quotation ID
        if (!activeQuotationNo && guestInfo?.guest_name?.trim() && (guestInfo?.guest_phone?.trim() || guestInfo?.guest_email?.trim())) {
            try {
                setLoading(true);
                const payload = buildPayload(false);
                const QRY_URL = `${process.env.REACT_APP_BASE_URL}/queries`;
                const res = await axios.post(`${QRY_URL}/create-queries`, payload);
                const created = res.data?.data || res.data;
                if (created?.quotation_no) {
                    activeQuotationNo = created.quotation_no;
                    activeInquiry = created;
                    setSavedQuotationNo(created.quotation_no);
                    setSavedInquiryId(created._id);
                    dispatch(setSelectedInquiry(created));
                }
            } catch (autoSaveErr) {
                console.warn('[PDF Export] Auto-save before export failed:', autoSaveErr);
            }
        }

        exportQuotationPDF(
            {
                guestInfo,
                tripDetails,
                stayInfo,
                itinerary,
                hotelSelections,
                allHotels,
                selectedPackage,
                carSeason,
                hotelSeason,
                cost,
                configData,
                currentMargin,
                detailedItinerary,
                dayWiseDetailedItinerary,
                carSelections,
                inclusions,
                exclusions,
                storedItineraries,
                allPackages,
                quotationNo: activeQuotationNo,
                inquiry: activeInquiry,
                currentUser,
            },
            setLoading,
            showSnackbar
        );
    };




    const handleEmailQuotation = async () => {
        if (!emailAddress || !/\S+@\S+\.\S+/.test(emailAddress)) {
            showSnackbar('Please enter a valid email address', 'error');
            return;
        }

        setEmailSending(true);
        // try {
        //     // Generate PDF as base64
        //     const canvas = await html2canvas(pdfRef.current, {
        //         scale: 2,
        //         useCORS: true,
        //     });
        //     const imgData = canvas.toDataURL('image/png');

        //     // Send email via your backend API
        //     await axios.post(`${process.env.REACT_APP_BASE_URL}/email/send-quotation`, {
        //         to: emailAddress,
        //         guestName: guestInfo.guest_name,
        //         pdfData: imgData,
        //         packageName: selectedPackage?.label || 'Tour Package',
        //         destination: tripDetails.location,
        //     });

        //     showSnackbar(`Quotation sent successfully to ${emailAddress}!`, 'success');
        //     setEmailDialogOpen(false);
        // } catch (error) {
        //     console.error('Error sending email:', error);
        //     showSnackbar('Failed to send email. Please try again.', 'error');
        // } finally {
        //     setEmailSending(false);
        // }
    };

    const resetForm = () => {
        setGuestInfo({
            guest_name: '',
            guest_email: '',
            guest_phone: '',
            guest_address: '',
            country_code: '+91'
        });
        setTripDetails({
            pax: '',
            kids_above_5: 0,
            car_details: [],
            location: '',
            keywords: '',
            travel_date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
            duration: '0',
            dropoff_location: 'NJP / IXB',
            important_notes: '',
        });
        setSelectedPackage(null);
        setHotelSelections({});
        setCost(0);
        setStayInfo({
            rooms: '',
        extra_mattress: '',
            hotel: '',
        });
        setEmailAddress('');
        setCarSeason('');
        setHotelSeason('');
        setCarSelections({});
        setSavedQuotationNo(null);
        setSavedInquiryId(null);
        loadedInquiryRef.current = null; // allow fresh load if same inquiry is reopened
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (dataLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={200} />
            </Box>
        );
    }

    const topOffset = onClose ? 0 : 64;
    const barHeight = 56;
    const sidebarTop = topOffset + barHeight;

    const scrollTo = (key) => {
        document.getElementById(`section-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <Box sx={{ bgcolor: '#f1f5f9', minHeight: '100%' }}>
            <Backdrop open={loading} sx={{ zIndex: 9999, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* ── Sticky Top Bar ─────────────────────────────────────── */}
            <Box sx={{
                position: 'sticky', top: topOffset, zIndex: 1000,
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5,
                boxShadow: '0 4px 24px rgba(0,0,0,0.35)', minHeight: barHeight,
            }}>
                {/* Back / Close */}
                <IconButton
                    onClick={onClose || (() => navigate(-1))}
                    size="small"
                    sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' }, flexShrink: 0 }}
                >
                    <ArrowBack fontSize="small" />
                </IconButton>

                {/* Title */}
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.1 }}>
                        {isEditMode ? '✏️ Edit Inquiry' : '📋 Create Inquiry'}
                    </Typography>
                    {(isEditMode || savedQuotationNo) && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>
                            {savedQuotationNo ? `Quotation: ${savedQuotationNo}` : 'Updating existing quotation'}
                        </Typography>
                    )}
                </Box>

                {/* Live summary chips */}
                <Stack direction="row" spacing={0.8} sx={{ flex: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {[
                        { label: guestInfo.guest_name, prefix: '👤' },
                        { label: tripDetails.location, prefix: '📍' },
                        { label: tripDetails.duration ? `${tripDetails.duration}N` : null, prefix: '📅' },
                        { label: tripDetails.pax ? `${tripDetails.pax} Pax` : null, prefix: '👥' },
                        {
                            label: selectedPackage?.label
                                ? (isPackageVerified(selectedPackage) ? `${selectedPackage.label} (Verified)` : selectedPackage.label)
                                : null,
                            prefix: isPackageVerified(selectedPackage) ? '🛡️' : '📦'
                        },
                    ].filter(c => c.label).map(c => (
                        <Chip
                            key={c.prefix}
                            label={<span style={{ fontSize: 11, fontWeight: 600 }}>{c.prefix} {c.label}</span>}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.15)', height: 24 }}
                        />
                    ))}
                </Stack>

                {/* Cost badge */}
                <Box sx={{ textAlign: 'right', flexShrink: 0, px: 1.5, py: 0.5, bgcolor: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', lineHeight: 1, display: 'block' }}>TOTAL</Typography>
                    <Typography variant="subtitle1" sx={{ color: '#4ade80', fontWeight: 900, lineHeight: 1.2 }}>
                        ₹{(Number(cost) || 0).toLocaleString('en-IN')}
                    </Typography>
                </Box>

                {/* Action buttons */}
                <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0 }}>
                    <Button variant="outlined" startIcon={<SaveAlt />} onClick={handleSaveAsDraft} disabled={loading} size="small"
                        sx={{ color: '#94a3b8', borderColor: '#334155', '&:hover': { borderColor: '#64748b', bgcolor: 'rgba(255,255,255,0.05)' }, fontSize: 12 }}>
                        Draft
                    </Button>
                    <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={handleExportPDF} disabled={loading} size="small"
                        sx={{ color: '#94a3b8', borderColor: '#334155', '&:hover': { borderColor: '#64748b', bgcolor: 'rgba(255,255,255,0.05)' }, fontSize: 12 }}>
                        PDF
                    </Button>
                    <Button variant="outlined" startIcon={<Email />} onClick={() => setEmailDialogOpen(true)} disabled={loading || (!isEditMode && !selectedPackage)} size="small"
                        sx={{ color: '#94a3b8', borderColor: '#334155', '&:hover': { borderColor: '#64748b', bgcolor: 'rgba(255,255,255,0.05)' }, fontSize: 12 }}>
                        Email
                    </Button>
                    <Button variant="contained" startIcon={isEditMode ? <EditIcon /> : <Save />} onClick={handleOpenPreview} disabled={loading || (!isEditMode && !selectedPackage)} size="small"
                        sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #667eea, #764ba2)', boxShadow: '0 4px 15px rgba(102,126,234,0.45)', fontSize: 12 }}>
                        {isEditMode ? 'Update' : 'Create'}
                    </Button>
                </Stack>
            </Box>

            {/* ── Body ─────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>

                {/* ── Sidebar navigation ── */}
                <Box sx={{
                    width: 200, flexShrink: 0,
                    position: 'sticky', top: sidebarTop,
                    height: `calc(100vh - ${sidebarTop}px)`,
                    overflowY: 'auto', bgcolor: '#fff',
                    borderRight: '1px solid #e2e8f0',
                    display: { xs: 'none', md: 'block' },
                }}>
                    <Box sx={{ p: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: 1, px: 1, display: 'block', mb: 1, fontSize: 10 }}>
                            SECTIONS
                        </Typography>
                        {SECTIONS.map(sec => (
                            <Box
                                key={sec.key}
                                onClick={() => scrollTo(sec.key)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 1.5, py: 0.9, borderRadius: 1.5, cursor: 'pointer', mb: 0.4,
                                    transition: 'all 0.15s',
                                    '&:hover': { bgcolor: `${sec.color}12`, '& .sec-label': { color: sec.color } },
                                }}
                            >
                                <Box sx={{
                                    width: 28, height: 28, borderRadius: 1.5, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: `${sec.color}18`, color: sec.color,
                                }}>
                                    {sec.icon}
                                </Box>
                                <Typography className="sec-label" variant="body2" sx={{ fontWeight: 500, color: '#475569', fontSize: 12, lineHeight: 1.3, transition: 'color 0.15s' }}>
                                    {sec.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Main content ── */}
                <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, minWidth: 0 }}>

                    {/* Guest Info */}
                    <Box id="section-guest" sx={{ scrollMarginTop: sidebarTop + 16, '& > .MuiPaper-root': { borderTop: '3px solid #667eea', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)' } }}>
                        <GuestInfoCard
                            guestInfo={guestInfo}
                            setGuestInfo={setGuestInfo}
                            handleGuestInfoChange={handleGuestInfoChange}
                            tripDetails={tripDetails}
                            setTripDetails={setTripDetails}
                            handleTripDetailsChange={handleTripDetailsChange}
                            snackbar={snackbar}
                            guestNameRef={guestNameRef}
                            onReset={handleGuestReset}
                        />
                    </Box>

                    {/* Trip Details */}
                    <Box id="section-trip" sx={{ scrollMarginTop: sidebarTop + 16, '& > .MuiPaper-root': { borderTop: '3px solid #f59e0b', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)' } }}>
                        <TripDetailsCard
                            tripDetails={tripDetails}
                            handleTripDetailsChange={handleTripDetailsChange}
                            setTripDetails={setTripDetails}
                            selectedPackage={selectedPackage}
                            stayInfo={stayInfo}
                            handleStayInfoChange={handleStayInfoChange}
                            setStayInfo={setStayInfo}
                            uniqueLocations={uniqueLocations}
                            snackbar={snackbar}
                            configData={configData}
                            onReset={handleTripDetailsReset}
                            handleLocationChange={handleLocationChange}
                            allPackages={allPackages}
                            onPackageSelect={handlePackageSelect}
                            carSeason={carSeason}
                            handleCarSeasonChange={handleCarSeasonChange}
                            handleCarDetailsChange={handleCarDetailsChange}
                            handleOptionalExtraToggle={handleOptionalExtraToggle}
                            handleOptionalExtraIncludeToggle={handleOptionalExtraIncludeToggle}
                            isLocationValid={isLocationValid}
                        />
                    </Box>

                    {/* Short Itinerary */}
                    <Box id="section-itinerary" sx={{ scrollMarginTop: sidebarTop + 16, '& > .MuiPaper-root': { borderTop: '3px solid #10b981', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)' } }}>
                        <ItineraryCard
                            itinerary={itinerary}
                            onItineraryChange={handleItineraryChange}
                            allPackages={allPackages}
                            dayWiseDetailedItinerary={dayWiseDetailedItinerary}
                            storedItineraries={storedItineraries}
                            isLocationValid={isLocationValid}
                            destination={tripDetails.location || ''}
                            showSnackbar={showSnackbar}
                            onCardCreated={(newCard) => {
                                setStoredItineraries(prev => {
                                    if (prev.some(c => c._id === newCard._id || c.title?.toLowerCase() === newCard.title?.toLowerCase())) {
                                        return prev;
                                    }
                                    return [newCard, ...prev];
                                });
                            }}
                            onCardUpdated={(updatedCard) => {
                                setStoredItineraries(prev => {
                                    const exists = prev.some(c => c._id === updatedCard._id);
                                    if (exists) {
                                        return prev.map(c => c._id === updatedCard._id ? updatedCard : c);
                                    }
                                    return [updatedCard, ...prev];
                                });
                            }}
                            onAddDay={() => {
                                if (!isLocationValid) {
                                    showSnackbar('Please select an approved location under Trip Details first', 'warning');
                                    return;
                                }
                                setItinerary([...itinerary, '']);
                                setTripDetails(prev => ({ ...prev, duration: (parseInt(prev.duration) || 0) + 1 }));
                            }}
                            onRemoveDay={(index) => {
                                const newItinerary = [...itinerary];
                                newItinerary.splice(index, 1);
                                setItinerary(newItinerary);
                                setTripDetails(prev => ({ ...prev, duration: Math.max(0, (parseInt(prev.duration) || 0) - 1) }));
                                setHotelSelections(prev => {
                                    const newSelections = {};
                                    let newIndex = 0;
                                    const sortedKeys = Object.keys(prev).map(Number).sort((a, b) => a - b);
                                    sortedKeys.forEach(key => { if (key !== index) { newSelections[newIndex] = prev[key]; newIndex++; } });
                                    return newSelections;
                                });
                                setCarSelections(prev => {
                                    const newSelections = {};
                                    let newIndex = 0;
                                    const sortedKeys = Object.keys(prev).map(Number).sort((a, b) => a - b);
                                    sortedKeys.forEach(key => { if (key !== index) { newSelections[newIndex] = prev[key]; newIndex++; } });
                                    const newTotalDays = Math.max(0, (parseInt(tripDetails.duration) || 0) - 1) + 1;
                                    const derived = deriveCarDetailsFromSelections(newSelections, newTotalDays);
                                    setTripDetails(tPrev => ({
                                        ...tPrev,
                                        car_details: derived
                                    }));
                                    return newSelections;
                                });
                            }}
                            onCopyText={(text) => {
                                navigator.clipboard.writeText(text);
                                showSnackbar('Day text copied to clipboard!', 'success');
                            }}
                            onDetailedItineraryChange={(newDetailed) => {
                                setDayWiseDetailedItinerary(newDetailed);
                                let html = '';
                                (newDetailed || []).forEach((dayDetail, idx) => {
                                    if (dayDetail) {
                                        const title = dayDetail.dayTitle || itinerary[idx] || `Day ${idx + 1}`;
                                        html += `<h3>${title}</h3>`;
                                        if (dayDetail.para && Array.isArray(dayDetail.para)) {
                                            dayDetail.para.forEach(p => {
                                                html += p;
                                            });
                                        }
                                    }
                                });
                                if (html) {
                                    setDetailedItinerary(html);
                                }
                            }}
                        />
                    </Box>

                    {/* Hotel Selection */}
                    <Box id="section-hotel" sx={{ scrollMarginTop: sidebarTop + 16, '& > .MuiPaper-root': { borderTop: '3px solid #3b82f6', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)' } }}>
                        <HotelSelectionCard
                            tripDetails={tripDetails}
                            allHotels={allHotels}
                            hotelSelections={hotelSelections}
                            stayInfo={stayInfo}
                            hotelSeason={hotelSeason}
                            handleHotelSeasonChange={handleHotelSeasonChange}
                            handleHotelReset={handleHotelReset}
                            selectedDay={selectedDay}
                            setSelectedDay={setSelectedDay}
                            handleHotelChange={handleHotelChange}
                            handleHotelBatchChange={handleHotelBatchChange}
                            handleAddHotelRoom={handleAddHotelRoom}
                            handleAddHotelExtra={handleAddHotelExtra}
                            handleRemoveHotelRoom={handleRemoveHotelRoom}
                            onViewHotel={handleViewHotel}
                        />
                    </Box>

                    {/* Car Selection */}
                    <Box id="section-car" sx={{ scrollMarginTop: sidebarTop + 16, '& > .MuiPaper-root': { borderTop: '3px solid #8b5cf6', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)' } }}>
                        <CarSelectionCard
                            tripDetails={tripDetails}
                            carSelections={carSelections}
                            carSeason={carSeason}
                            itinerary={itinerary}
                            handleCarSeasonChange={handleCarSeasonChange}
                            handleCarReset={handleCarReset}
                            handleCarSelectionChange={handleCarSelectionChange}
                            configData={configData}
                        />
                    </Box>

                    {/* Inclusions / Exclusions */}
                    <Box id="section-inclusions" sx={{ scrollMarginTop: sidebarTop + 16, '& > .MuiPaper-root': { borderTop: '3px solid #06b6d4', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)' } }}>
                        <InclusionsExclusionsEditor
                            inclusions={inclusions}
                            setInclusions={setInclusions}
                            exclusions={exclusions}
                            setExclusions={setExclusions}
                        />
                    </Box>

                    {/* Additional Services */}
                    <Box id="section-extras" sx={{ scrollMarginTop: sidebarTop + 16, '& > .MuiPaper-root': { borderTop: '3px solid #f43f5e', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)' } }}>
                        <AdditionalInclusionsCard
                            tripDetails={tripDetails}
                            setTripDetails={setTripDetails}
                            handleOptionalExtraIncludeToggle={handleOptionalExtraIncludeToggle}
                        />
                    </Box>

                    {/* Important Notes */}
                    <Box id="section-notes" sx={{ scrollMarginTop: sidebarTop + 16, mb: 3 }}>
                        <Paper sx={{ p: 0, borderTop: '3px solid #6366f1', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fafbff', borderBottom: '1px solid #e8eaf6' }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: '#6366f118', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1' }}>
                                    <StickyNote2 sx={{ fontSize: 17 }} />
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                    Important Notes
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2.5 }}>
                                <TextField
                                    fullWidth
                                    name="important_notes"
                                    value={tripDetails.important_notes || ''}
                                    onChange={handleTripDetailsChange}
                                    placeholder="Enter important notes, terms, or special instructions here..."
                                    multiline
                                    minRows={4}
                                    variant="outlined"
                                    sx={{ "& textarea": { resize: "vertical" }, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                />
                            </Box>
                        </Paper>
                    </Box>

                    {/* Cost Estimate */}
                    <Box id="section-cost" sx={{ scrollMarginTop: sidebarTop + 16, '& > .MuiPaper-root': { borderTop: '3px solid #22c55e', borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.07)' } }}>
                        <CostEstimateCard
                            selectedPackage={selectedPackage}
                            cost={cost}
                            handleCostChange={handleCostChange}
                            hotelSelections={hotelSelections}
                            allHotels={allHotels}
                            stayInfo={stayInfo}
                            tripDetails={tripDetails}
                            configData={configData}
                            carSeason={carSeason}
                            hotelSeason={hotelSeason}
                            currentMargin={currentMargin}
                            setCurrentMargin={setCurrentMargin}
                            carSelections={carSelections}
                            isB2B={isB2B}
                            setIsB2B={setIsB2B}
                        />
                    </Box>

                    {/* ── Bottom Action Bar ── */}
                    <Paper sx={{
                        p: 2, mb: 3, borderRadius: 2,
                        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                        display: 'flex', gap: 1.5, alignItems: 'center',
                    }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>Ready to save?</Typography>
                            <Typography sx={{ color: '#4ade80', fontWeight: 800, fontSize: 18 }}>
                                ₹{(Number(cost) || 0).toLocaleString('en-IN')}
                            </Typography>
                        </Box>
                        {!isEditMode && (!selectedPackage || selectedPackage?._id === 'custom') && (
                            <Button variant="contained" color="secondary" startIcon={<Save />} onClick={handleCreatePackage} disabled={loading} sx={{ fontWeight: 700, px: 3 }}>
                                {loading ? 'Creating...' : 'Create Package'}
                            </Button>
                        )}
                        <Button
                            variant="contained" startIcon={isEditMode ? <EditIcon /> : <Save />}
                            onClick={handleOpenPreview} disabled={loading || (!isEditMode && !selectedPackage)}
                            sx={{ fontWeight: 700, px: 4, background: 'linear-gradient(135deg, #667eea, #764ba2)', boxShadow: '0 4px 15px rgba(102,126,234,0.5)' }}
                        >
                            {loading ? 'Saving...' : isEditMode ? 'Update Inquiry' : 'Create Inquiry'}
                        </Button>
                        {!isEditMode && (
                            <Button variant="outlined" startIcon={<Clear />} onClick={onClose || resetForm} disabled={loading}
                                sx={{ color: '#94a3b8', borderColor: '#334155', '&:hover': { borderColor: '#64748b', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                {onClose ? 'Cancel' : 'Reset'}
                            </Button>
                        )}
                    </Paper>
                </Box>
            </Box>

            {/* Detailed Itinerary Dialog (before PDF export) */}
            <Dialog open={itineraryDialogOpen} onClose={() => setItineraryDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Detailed Itinerary</DialogTitle>
                <DialogContent>
                    {/* Hotel Selections Preview — helps verify current data before PDF generation */}
                    {(() => {
                        const hotelRows = [];
                        Object.keys(hotelSelections).sort((a, b) => Number(a) - Number(b)).forEach(d => {
                            const arr = Array.isArray(hotelSelections[d]) ? hotelSelections[d] : (hotelSelections[d] ? [hotelSelections[d]] : []);
                            arr.forEach(sel => {
                                if (sel && sel.hotelId) {
                                    const hotel = allHotels.find(h => h._id?.toString() === sel.hotelId?.toString());
                                    hotelRows.push({ night: Number(d) + 1, name: hotel?.hotel_name || `ID: ${sel.hotelId}`, roomType: sel.roomType || '—', mealPlan: sel.mealPlan || '—' });
                                }
                            });
                        });
                        if (hotelRows.length === 0) return null;
                        return (
                            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #bae6fd' }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0369a1', mb: 0.5 }}>
                                    Hotel Selections to be included in PDF ({hotelRows.length} night{hotelRows.length !== 1 ? 's' : ''})
                                </Typography>
                                {hotelRows.map((r, i) => (
                                    <Typography key={i} sx={{ fontSize: 11, color: '#0c4a6e' }}>
                                        Night {r.night}: <strong>{r.name}</strong> · {r.roomType} · {r.mealPlan}
                                    </Typography>
                                ))}
                            </Box>
                        );
                    })()}

                    <Autocomplete
                        options={storedItineraries}
                        getOptionLabel={(option) => option.title}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                let html = '';
                                newValue.itinerary?.forEach(day => {
                                    const headerText = day.headerTag ? `${day.headerTag}: ${day.headerValue || ''}` : (day.headerValue || '');
                                    html += `<h3>${headerText}</h3>`;
                                    if (day.Distance || day.Duration || day.Altitude || day.Time) {
                                        html += `<p>`;
                                        if (day.Distance) html += `<strong>Distance:</strong> ${day.Distance} &nbsp;`;
                                        if (day.Duration) html += `<strong>Duration:</strong> ${day.Duration} &nbsp;`;
                                        if (day.Altitude) html += `<strong>Altitude:</strong> ${day.Altitude} &nbsp;`;
                                        if (day.Time) html += `<strong>Time:</strong> ${day.Time}`;
                                        html += `</p>`;
                                    }
                                    if (day.para && day.para.length > 0) {
                                        day.para.forEach(p => {
                                            html += p;
                                        });
                                    }
                                });
                                setDetailedItinerary(html);
                            }
                        }}
                        renderInput={(params) => <TextField {...params} label="Load from Itinerary Store" variant="outlined" margin="normal" />}
                    />
                    <Box sx={{ mt: 2, mb: 2, '& .ql-container': { minHeight: '200px', resize: 'vertical', overflowY: 'auto' } }}>
                        <ReactQuill
                            theme="snow"
                            value={detailedItinerary}
                            onChange={setDetailedItinerary}
                            placeholder="Type or paste the detailed itinerary here..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setItineraryDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmExportPDF}
                        variant="contained"
                        startIcon={<PictureAsPdf />}
                    >
                        Export PDF
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Email Dialog */}
            <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Send Quotation via Email</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="guest@example.com"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEmailQuotation}
                        variant="contained"
                        disabled={emailSending}
                        startIcon={emailSending ? <CircularProgress size={20} /> : <Email />}
                    >
                        {emailSending ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Inquiry Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f5f5' }}>
                    Preview Inquiry Details
                    <Box>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PictureAsPdf />}
                            onClick={handleConfirmExportPDF}
                            sx={{ mr: 1 }}
                        >
                            Download PDF
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Save />}
                            onClick={() => {
                                setPreviewOpen(false);
                                handleSubmit(false);
                            }}
                        >
                            Confirm & {isEditMode ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: '#eee' }}>
                    <InquiryPreview
                        guestInfo={guestInfo}
                        tripDetails={tripDetails}
                        stayInfo={stayInfo}
                        itinerary={itinerary}
                        hotelSelections={hotelSelections}
                        allHotels={allHotels}
                        selectedPackage={selectedPackage}
                        cost={cost}
                        detailedItinerary={detailedItinerary}
                        onDetailedItineraryChange={setDetailedItinerary}
                        dayWiseDetailedItinerary={dayWiseDetailedItinerary}
                        storedItineraries={storedItineraries}
                        allPackages={allPackages}
                        carSelections={carSelections}
                        inclusions={inclusions}
                        exclusions={exclusions}
                        quotationNo={savedQuotationNo || existingInquiry?.quotation_no || reduxSelectedInquiry?.quotation_no}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Back to Edit</Button>
                </DialogActions>
            </Dialog>

            {/* Package Change Detection Dialog */}
            {/* Package Action Dialog — shown on every Update/Create Inquiry click when package data changed */}
            {(() => {
                const hasExistingPkg = selectedPackage && selectedPackage._id !== 'custom';
                const isPkgVerified = isPackageVerified(selectedPackage);
                const changed = hasPackageDataChanged();
                return (
                    <Dialog open={pkgChangeDialogOpen} onClose={() => setPkgChangeDialogOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isPkgVerified ? (
                                <Shield color="success" />
                            ) : (
                                <SystemUpdateAlt color="warning" />
                            )}
                            {hasExistingPkg
                                ? (isPkgVerified ? 'Verified Package — Create New Package' : 'Package Options')
                                : 'Create a Package?'}
                        </DialogTitle>
                        <DialogContent>
                            {hasExistingPkg ? (
                                isPkgVerified ? (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <strong>"{selectedPackage.label}"</strong> is a <strong>Verified Package</strong> and cannot be overwritten or updated directly.
                                        <br />
                                        To save your customized itinerary, please create a new package for this quotation, or save the inquiry without modifying the package.
                                    </Alert>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {changed
                                            ? <>You've modified itinerary, inclusions, or exclusions linked to <strong>"{selectedPackage.label}"</strong> (Not Verified). You can update this package or create a new one.</>
                                            : <>Linked package: <strong>"{selectedPackage.label}"</strong>. Choose an option or just save the inquiry.</>
                                        }
                                    </Typography>
                                )
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    No package is linked to this inquiry. You can create one from the current itinerary &amp; inclusions, or save without a package.
                                </Typography>
                            )}
                            <Stack spacing={1.5}>
                                {/* Only non-verified packages can be updated */}
                                {hasExistingPkg && !isPkgVerified && (
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        startIcon={<SystemUpdateAlt />}
                                        onClick={handleUpdateExistingPackage}
                                        fullWidth
                                        sx={{ justifyContent: 'flex-start', px: 2, textTransform: 'none' }}
                                    >
                                        Update existing package with current itinerary &amp; inclusions
                                    </Button>
                                )}
                                {/* Both verified and unverified can create a new package */}
                                <Button
                                    variant={isPkgVerified ? 'contained' : (hasExistingPkg ? 'outlined' : 'contained')}
                                    color="primary"
                                    startIcon={<AddBox />}
                                    onClick={handleCreateNewPackageFromDialog}
                                    fullWidth
                                    sx={{ justifyContent: 'flex-start', px: 2, textTransform: 'none' }}
                                >
                                    {hasExistingPkg
                                        ? (isPkgVerified
                                            ? 'Create a new package from current changes (Recommended)'
                                            : 'Create a new package from current changes')
                                        : 'Create package from this inquiry'}
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={handleSaveWithoutPackageChange}
                                    fullWidth
                                    sx={{ justifyContent: 'flex-start', px: 2, color: 'text.secondary', textTransform: 'none' }}
                                >
                                    Save inquiry without modifying/creating any package
                                </Button>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setPkgChangeDialogOpen(false)}>Cancel</Button>
                        </DialogActions>
                    </Dialog>
                );
            })()}

            {/* Hotel Drawer */}
            <HotelDrawer
                open={hotelDrawerOpen}
                onClose={() => setHotelDrawerOpen(false)}
                mode="view"
                selectedHotel={selectedHotelToView}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CreateInquiry;
